import React, { useEffect } from "react";
import { Modal, Box, Typography, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import UploadWatchContent from "./UploadWatchContent";
import { useNavigate, useLocation } from "react-router-dom";
import OneDrivePicker from "./OneDrivePicker2";
import useCategorizeFiles from "../../hooks/useCategorizeFiles";
import ViewCSVData from "./ViewCSVData";
import useHandleFilePath from "../../hooks/useHandleFilePath";
import { setCSVBatchData } from "../../redux/slices/contentSlice/contentForm/contentForm";
import { setSelectedOneDriveFiles } from "../../redux/slices/contentSlice/onedriveContent/onedriveContent";
import UploadFile from "@mui/icons-material/UploadFile";
import { useCallback } from "react";
import { CheckCircle } from "@material-ui/icons";
import FormErrorMessageBeforeUploading from "../../shared/ErrorMessages/FormErrorMessageBeforeUploading";

const UploadMetaData = ({
	formik,
	masterFileContent,
	trailerContent,
	setMasterFileContent,
	setTrailerContent,
	setSelectedPosterFiles,
}) => {
	const [open, setOpen] = React.useState(false);
	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);
	const [isLocalFilesUploaded, setIsLocalFilesUploaded] =
		React.useState(false);
	const oneDriveFiles = useSelector((state) => state.oneDriveContent.files);

	const {
		videos,
		images,
		audios,
		csv,
		pdfs,
		loading,
		subtitles,
		clearFiles,
	} = useCategorizeFiles(oneDriveFiles, "onedrive");

	const memoizedClearFiles = useCallback(clearFiles, [clearFiles]);

	const { handleFilePath, handleSubtitleFilePath } = useHandleFilePath();

	const dispatch = useDispatch();

	useEffect(() => {
		// Set OneDrive flag when files are present
		if (oneDriveFiles?.length > 0) {
			formik.setFieldValue("isOneDrive", true);
		}

		// Process videos with enhanced master/trailer detection
		if (videos.length > 0) {
			console.log(`Processing ${videos.length} video file(s)...`);
			
			// Separate master and trailer videos
			const masterVideos = videos.filter(video => video.type === "master");
			const trailerVideos = videos.filter(video => video.type === "trailer");
			
			console.log(`Found ${masterVideos.length} master video(s) and ${trailerVideos.length} trailer video(s)`);

			videos.forEach(async (video, index) => {
				try {
					const { filePath } = handleFilePath(video, formik.values.title);

					if (video.type === "master") {
						formik.setFieldValue("filePath", filePath);
						console.log("✅ Master video file path set:", filePath);
					} else if (video.type === "trailer") {
						formik.setFieldValue("trailerFilePath", filePath);
						console.log("✅ Trailer video file path set:", filePath);
					}
					
					dispatch(
						setSelectedOneDriveFiles({
							filePath: filePath,
							downloadUrl: video.downloadUrl,
						})
					);
				} catch (error) {
					console.error("Error processing video file:", error);
				}
			});

			// Validation: Ensure we have at least one master file
			if (masterVideos.length === 0) {
				console.warn("⚠️ No master video file detected!");
				// Clear the filePath if no master video is found
				if (!formik.values.filePath || formik.values.filePath === "") {
					formik.setFieldError("filePath", "Master file has not been uploaded yet!");
				}
			}
		}

		// Process audio files
		if (audios.length > 0) {
			console.log(`Processing ${audios.length} audio file(s)...`);
			audios.forEach((audio) => {
				try {
					const { filePath } = handleFilePath(audio, formik.values.title);
					formik.setFieldValue("filePath", filePath);
					console.log("✅ Audio file path set:", filePath);
					
					dispatch(
						setSelectedOneDriveFiles({
							filePath: filePath,
							downloadUrl: audio.downloadUrl,
						})
					);
				} catch (error) {
					console.error("Error processing audio file:", error);
				}
			});
		}

		// Process image files (posters) with enhanced validation
		if (images.length > 0) {
			console.log(`Processing ${images.length} poster file(s)...`);
			try {
				const posters = images.map((image) => {
					const { filePath } = handleFilePath(image, formik.values.title);
					
					dispatch(
						setSelectedOneDriveFiles({
							filePath: filePath,
							downloadUrl: image.downloadUrl,
						})
					);
					
					return filePath;
				});

				// Enhanced poster validation
				const validPosters = posters.filter(poster => 
					poster && 
					poster.trim() !== "" && 
					poster !== "undefined" &&
					!poster.includes("undefined")
				);

				if (validPosters.length > 0) {
					formik.setFieldValue("posters", validPosters);
					console.log("✅ Posters processed successfully:", validPosters.length);
					console.log("Poster paths:", validPosters);
					
					// Clear any poster errors
					if (formik.errors.posters) {
						formik.setFieldError("posters", "");
					}
				} else {
					console.warn("⚠️ No valid poster paths generated");
					formik.setFieldValue("posters", [""]);
				}
			} catch (error) {
				console.error("Error processing poster files:", error);
				formik.setFieldValue("posters", [""]);
			}
		} else if (oneDriveFiles?.length > 0 && !loading) {
			// If OneDrive files are present but no images were categorized
			console.warn("⚠️ OneDrive files present but no images categorized");
		}

		// Process PDF files
		if (pdfs.length > 0) {
			console.log(`Processing ${pdfs.length} PDF file(s)...`);
			pdfs.forEach((pdf) => {
				try {
					const { filePath } = handleFilePath(pdf, formik.values.title);
					formik.setFieldValue("filePath", filePath);
					console.log("✅ PDF file path set:", filePath);
					
					dispatch(
						setSelectedOneDriveFiles({
							filePath: filePath,
							downloadUrl: pdf.downloadUrl,
						})
					);
				} catch (error) {
					console.error("Error processing PDF file:", error);
				}
			});
		}

		// Process subtitle files
		if (subtitles.length > 0) {
			console.log(`Processing ${subtitles.length} subtitle file(s)...`);
			subtitles.forEach((subtitle) => {
				try {
					const { filePath } = handleFilePath(
						subtitle,
						formik.values?.title,
						formik.values?.language
					);
					console.log("subtitle", filePath);
					formik.setFieldValue("masterSubtitleFilePath", filePath);
					
					dispatch(
						setSelectedOneDriveFiles({
							filePath: filePath,
							downloadUrl: subtitle.downloadUrl,
						})
					);
				} catch (error) {
					console.error("Error processing subtitle file:", error);
				}
			});
		}
	}, [videos, images, audios, pdfs, loading, subtitles, formik.values.title]);

	// Clear files when title changes
	useEffect(() => {
		if (!formik.values.title) {
			memoizedClearFiles();
			return;
		}
	}, [formik.values.title, memoizedClearFiles]);

	// Enhanced button disable logic
	const handleDisableButton = () => {
		// Check for form errors in required fields
		const requiredFieldErrors = [
			formik?.errors?.title,
			formik?.errors?.category,
			formik?.errors?.type,
			formik?.errors?.language
		].some(error => error);

		// Also disable while loading
		if (loading) {
			return true;
		}

		return requiredFieldErrors;
	};

	// Enhanced file validation status
	const getFileValidationStatus = () => {
		const masterVideos = videos.filter(video => video.type === "master");
		const hasValidPosters = formik.values.posters?.length > 0 && 
			formik.values.posters.some(poster => poster && poster.trim() !== "");

		return {
			hasMasterFile: masterVideos.length > 0 || audios.length > 0 || pdfs.length > 0,
			hasPosters: hasValidPosters,
			hasTrailer: videos.filter(video => video.type === "trailer").length > 0,
			hasSubtitles: subtitles.length > 0
		};
	};

	const fileStatus = getFileValidationStatus();

	console.log("UploadMetaData formik values:", {
		title: formik.values.title,
		posters: formik.values.posters,
		filePath: formik.values.filePath,
		trailerFilePath: formik.values.trailerFilePath,
		isOneDrive: formik.values.isOneDrive,
		oneDriveFilesCount: oneDriveFiles?.length || 0,
		imagesCount: images.length,
		videosCount: videos.length,
		loading,
		fileStatus
	});

	return (
		<>
			<Typography variant="h6">Upload The Content</Typography>
			<Typography color={"primary.light"} fontSize={12} variant="p">
				(all files including metadata)
			</Typography>
			<Box
				display={"flex"}
				justifyContent={"space-between"}
				alignItems={"center"}
				gap={2}
				sx={{ marginTop: "10px" }}
			>
				<Box flexGrow={1} flexBasis={"50%"}>
					<Button
						fullWidth
						variant="contained"
						onClick={handleOpen}
						disabled={handleDisableButton()}
						color={isLocalFilesUploaded ? "success" : "primary"}
					>
						{isLocalFilesUploaded ? (
							<>
								<CheckCircle />
								Files Uploaded
							</>
						) : (
							<>
								<UploadFile />
								Upload From Local Drive
							</>
						)}
					</Button>
					<FormErrorMessageBeforeUploading errors={formik.errors} />
				</Box>
				<Typography>OR</Typography>
				<Box flexGrow={1} flexBasis={"50%"}>
					<OneDrivePicker
						handleDisableButton={handleDisableButton}
						formik={formik}
					/>
				</Box>
			</Box>

			{loading ? (
				<Box sx={{ marginTop: 2 }}>
					<Typography>Loading files from OneDrive...</Typography>
					<Typography variant="caption" color="text.secondary">
						Processing uploaded files, please wait...
					</Typography>
				</Box>
			) : null}

			{/* Enhanced file processing status */}
			{!loading && oneDriveFiles?.length > 0 && (
				<Box sx={{ marginTop: 2 }}>
					<Typography variant="body2" color="success.main">
						✅ OneDrive files processed:
					</Typography>
					<Typography variant="caption" component="div">
						• Videos: {videos.length} (Master: {videos.filter(v => v.type === "master").length}, Trailer: {videos.filter(v => v.type === "trailer").length})
					</Typography>
					<Typography variant="caption" component="div">
						• Posters: {images.length}
					</Typography>
					<Typography variant="caption" component="div">
						• Audio: {audios.length}
					</Typography>
					<Typography variant="caption" component="div">
						• Subtitles: {subtitles.length}
					</Typography>
				</Box>
			)}

			{/* File validation warnings */}
			{!loading && oneDriveFiles?.length > 0 && (
				<Box sx={{ marginTop: 1 }}>
					{!fileStatus.hasMasterFile && (
						<Typography variant="caption" color="warning.main">
							⚠️ No master content file detected
						</Typography>
					)}
					{!fileStatus.hasPosters && (
						<Typography variant="caption" color="warning.main" display="block">
							⚠️ No poster images detected
						</Typography>
					)}
				</Box>
			)}

			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<UploadWatchContent
					formik={formik}
					masterFileContent={masterFileContent}
					trailerContent={trailerContent}
					setOpen={setOpen}
					setMasterFileContent={setMasterFileContent}
					setTrailerContent={setTrailerContent}
					setIsLocalFilesUploaded={setIsLocalFilesUploaded}
					setSelectedPosterFiles={setSelectedPosterFiles}
				/>
			</Modal>
			
			{/* Enhanced error display */}
			<>
				{(formik.touched.filePath || formik.touched.trailerFilePath) &&
					(formik.errors.filePath ||
						formik.errors.trailerFilePath) && (
						<Typography sx={{ color: "red", marginTop: 1 }}>
							{formik.errors.filePath ||
								formik.errors.trailerFilePath}
						</Typography>
					)}
				
				{/* Show poster validation errors */}
				{formik.touched.posters && formik.errors.posters && (
					<Typography sx={{ color: "red", marginTop: 1 }}>
						{formik.errors.posters}
					</Typography>
				)}
			</>
		</>
	);
};

export default UploadMetaData;